import { Card, CardContent, Link, Typography } from '@material-ui/core';
import { Help, WarningRounded, WarningSharp } from '@material-ui/icons';
import {
  FormattedDate,
  NullableDateString,
  parseDate,
} from '@superdispatch/dates';
import {
  Color,
  Column,
  Columns,
  Image,
  Inline,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { ReactNode, useMemo } from 'react';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { PermissionAction } from 'shared/modules/permission/core/PermissionAction';
import { usePermissionAccess } from 'shared/modules/permission/data/usePermissionAccess';
import { LinkButton } from 'shared/routing/Links';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import styled from 'styled-components';
import microDepositIllustration from '../../assets/micro_deposit_illustration.png';
import microDepositIllustrationDark from '../../assets/micro_deposit_illustration_dark.png';
import { useReadyForVerification } from './useVerificationAvailable';

const ExpirationText = styled(Typography)`
  color: ${Color.Yellow300};
`;

interface BankVerificationProps {
  bankName?: string | null;
  bankAccountDisplayNumber?: string | null;
  bankAccountDateSent?: NullableDateString;
  bankAccountAvailableDate: NullableDateString;
  hasAccountFeature?: boolean;
  isVerificationAvailable: boolean;
  path: string;
}

export function BankVerification({
  bankAccountDisplayNumber,
  bankName,
  bankAccountDateSent,
  hasAccountFeature,
  isVerificationAvailable,
  bankAccountAvailableDate,
  path,
}: BankVerificationProps) {
  return (
    <Stack space="small">
      <Inline horizontalAlign="right" verticalAlign="center" space="xxsmall">
        <Help fontSize="small" color="action" />
        <WebViewLink
          target="_blank"
          rel="noopener noreferrer"
          href="https://support.superdispatch.com/hc/articles/35595408017939-How-To-Setup-SuperPay-In-Carrier-TMS"
        >
          How to set up SuperPay
        </WebViewLink>
      </Inline>
      <Card aria-label="superpay bank verification banner">
        <CardContent>
          {isVerificationAvailable ? (
            <ReadyForVerification
              bankName={bankName}
              bankAccountDisplayNumber={bankAccountDisplayNumber}
              bankAccountDateSent={bankAccountDateSent}
              hasAccountFeature={hasAccountFeature}
              path={path}
            />
          ) : (
            <WaitingForDeposits
              bankName={bankName}
              bankAccountDisplayNumber={bankAccountDisplayNumber}
              bankAccountAvailableDate={bankAccountAvailableDate}
              hasAccountFeature={hasAccountFeature}
              path={path}
            />
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

interface BankAccountLabelProps {
  bankName?: string | null;
  bankAccountDisplayNumber?: string | null;
}

function InstantMicrodepositDescription({
  bankName,
  bankAccountDisplayNumber,
}: BankAccountLabelProps) {
  return (
    <Typography>
      You should see one small deposit of <b>$0.01</b> to your{' '}
      <b>{bankName || 'bank'}</b> account
      {bankAccountDisplayNumber && (
        <>
          {' '}
          ending in <b>{bankAccountDisplayNumber}</b>
        </>
      )}
      . On your bank account it appears with the description containing code{' '}
      <b>MV****</b> followed by a 4-digit.
    </Typography>
  );
}

interface CardLayoutProps {
  title: ReactNode;
  body: ReactNode;
}

function CardLayout({ title, body }: CardLayoutProps) {
  const isMobile = useResponsiveValue(true, false);

  return (
    <Columns space="large">
      <Column>
        <Stack space="small">
          <Columns space="small">
            <Column>
              <Typography variant="h4">{title}</Typography>
            </Column>

            {isMobile && (
              <Column width="content">
                <Box width="90px" height="52px">
                  <Image
                    src={microDepositIllustration}
                    srcDark={microDepositIllustrationDark}
                    width="100%"
                  />
                </Box>
              </Column>
            )}
          </Columns>

          <Box maxWidth={['75%', 'initial', 'initial']}>{body}</Box>
        </Stack>
      </Column>

      {!isMobile && (
        <Column width="content">
          <Image
            src={microDepositIllustration}
            srcDark={microDepositIllustrationDark}
            width="109px"
          />
        </Column>
      )}
    </Columns>
  );
}

function ContactSupportFooter({ message }: { message: ReactNode }) {
  const { showLauncherMessanger } = useCustomerSupport();

  return (
    <Typography variant="caption" color="textSecondary">
      <Link
        onClick={() => {
          showLauncherMessanger();
        }}
        component="button"
      >
        Contact support
      </Link>
      &nbsp;{message}
    </Typography>
  );
}

interface ReadyForVerificationProps extends BankAccountLabelProps {
  bankAccountDateSent?: NullableDateString;
  hasAccountFeature?: boolean;
  path: string;
}

function ReadyForVerification({
  bankName,
  bankAccountDisplayNumber,
  bankAccountDateSent,
  hasAccountFeature,
  path,
}: ReadyForVerificationProps) {
  const isReadyForVerification = useReadyForVerification();
  const hasNotEditAccess = usePermissionAccess({
    name: 'superpay',
    permission: 'can_edit',
  });

  const expirationDate = useMemo(() => {
    const accountInitiatedDate = parseDate(bankAccountDateSent, {
      format: 'DateISO',
    });
    return accountInitiatedDate.plus({ days: 14 });
  }, [bankAccountDateSent]);

  return (
    <Stack space="small">
      <CardLayout
        title="Ready for Bank Account Verification"
        body={
          <Stack space="small">
            <InstantMicrodepositDescription
              bankName={bankName}
              bankAccountDisplayNumber={bankAccountDisplayNumber}
            />
          </Stack>
        }
      />
      <Box padding="small" borderRadius="small" backgroundColor="Yellow50">
        <Inline verticalAlign="center">
          <WarningSharp fontSize="small" htmlColor={Color.Yellow300} />
          <ExpirationText>
            Deposits expire on&nbsp;
            <FormattedDate variant="ShortDate" date={expirationDate} />
          </ExpirationText>
        </Inline>
      </Box>
      {!hasAccountFeature && <AccountingFeatureHint />}

      <PermissionAction name="superpay" permission="can_edit">
        <LinkButton
          to={`${path}/verify-bank-account?utm_medium=SuperPay Settings`}
          disabled={
            !hasAccountFeature || !isReadyForVerification || hasNotEditAccess
          }
        >
          Verify Bank Account
        </LinkButton>
      </PermissionAction>

      <ContactSupportFooter message="if transactions haven’t appeared or you want to change bank account." />
    </Stack>
  );
}

interface WaitingForDepositsProps extends BankAccountLabelProps {
  bankAccountAvailableDate: NullableDateString;
  hasAccountFeature?: boolean;
  path: string;
}

function WaitingForDeposits({
  bankName,
  bankAccountDisplayNumber,
  bankAccountAvailableDate,
  hasAccountFeature,
  path,
}: WaitingForDepositsProps) {
  return (
    <Stack space="small">
      <CardLayout
        title={
          <>
            Transactions should appear by{' '}
            <b>
              <FormattedDate
                variant="ShortDate"
                date={bankAccountAvailableDate}
              />
            </b>
          </>
        }
        body={
          <Stack space="small">
            <InstantMicrodepositDescription
              bankName={bankName}
              bankAccountDisplayNumber={bankAccountDisplayNumber}
            />
          </Stack>
        }
      />

      {!hasAccountFeature && <AccountingFeatureHint />}
      <Stack space="xsmall">
        <LinkButton
          to={`${path}/verify-bank-account?utm_medium=SuperPay Settings`}
          disabled={true}
        >
          Verify Bank Account
        </LinkButton>

        <Inline verticalAlign="center">
          <WarningRounded fontSize="small" htmlColor={Color.Yellow300} />
          <ExpirationText variant="caption">
            The Verify Bank Account button will be available by the end of&nbsp;
            <FormattedDate
              variant="ShortDate"
              date={bankAccountAvailableDate}
            />
            .
          </ExpirationText>
        </Inline>
      </Stack>

      <ContactSupportFooter message="if transactions haven’t appeared or you want to change bank account." />
    </Stack>
  );
}

function AccountingFeatureHint() {
  return (
    <Typography>
      To verify bank account, you need to have the accounting feature enabled.
      Contact manager to resolve the issue.
    </Typography>
  );
}
